import { useCallback, useMemo } from 'react';
import {
  draftStore,
  isPersistenceUnavailable,
  markPersistenceUnavailable,
  PERSISTED_ENTRY_TTL_MS,
} from '../services/persistence';

type PersistedDraft = {
  value: string;
  savedAt: number;
};

interface DraftPersistenceOptions {
  gameKey: string;
  stackId: string;
  entryType: 'prompt' | 'guess';
  entryNumber?: number;
}

const buildStorageKey = ({ gameKey, stackId, entryType, entryNumber }: DraftPersistenceOptions) =>
  `draft:${gameKey}:${stackId}:${entryType}:${entryNumber ?? 'initial'}`;

export const useDraftPersistence = ({
  gameKey,
  stackId,
  entryType,
  entryNumber
}: DraftPersistenceOptions) => {
  const storageKey = useMemo(
    () => buildStorageKey({ gameKey, stackId, entryType, entryNumber }),
    [gameKey, stackId, entryType, entryNumber]
  );

  const loadDraft = useCallback(async () => {
    let saved: PersistedDraft | null;
    try {
      saved = await draftStore.getItem<PersistedDraft>(storageKey);
    } catch (error) {
      markPersistenceUnavailable(error);
      return null;
    }
    if (!saved || !saved.value || !saved.savedAt) {
      return null;
    }

    if (Date.now() - saved.savedAt > PERSISTED_ENTRY_TTL_MS) {
      await draftStore.removeItem(storageKey);
      return null;
    }

    return saved.value;
  }, [storageKey]);

  const saveDraft = useCallback(
    async (value: string) => {
      if (!value || isPersistenceUnavailable()) return;
      const payload: PersistedDraft = {
        value,
        savedAt: Date.now()
      };
      try {
        await draftStore.setItem(storageKey, payload);
      } catch (error) {
        markPersistenceUnavailable(error);
      }
    },
    [storageKey]
  );

  const clearDraft = useCallback(async () => {
    try {
      await draftStore.removeItem(storageKey);
    } catch (error) {
      markPersistenceUnavailable(error);
    }
  }, [storageKey]);

  return {
    storageKey,
    loadDraft,
    saveDraft,
    clearDraft
  };
};
